import client from 'axios'
import store from '@/store'
import _ from 'lodash'
import { env } from '@/config/runtime-env'
import {
  readAccessToken,
  refreshAccessToken
} from '@/store/modules/auth/refresh'

/**
 * Whether this failure is the credential rather than the request.
 *
 * Two shapes, because the backend produces two. An expired or malformed access
 * token is raised by fastapi-jwt-auth as a 422 in its own envelope; a session
 * row that no longer matches the token is a real 401 in the standard one. Both
 * are worth exactly one renewal: the first is what refreshing is for, and the
 * second is what a session whose token has been rotated looks like.
 */
function staleCredential(error: any): boolean {
  const status = _.get(error, 'response.status')

  if (status === 422) {
    return (
      String(_.get(error, 'response.data.message', '')).indexOf(
        'Exception Occurred in Auth Tokens!'
      ) > -1
    )
  }

  if (status === 401) {
    return _.get(error, 'response.data.info.message') === 'Invalid session token'
  }

  return false
}

client.defaults.baseURL = env.VUE_APP_API_BASE
client.interceptors.request.use(
  function(config) {
    // In memory since SMN-68, not a cookie any script can read.
    const token = readAccessToken()

    // Only when there is one. The old condition also attached the header when
    // the token was absent, sending a literal `Bearer null` on every request
    // that was not the subscriber report. That was survivable while a token was
    // almost always present; with the token now living in memory, every page
    // load begins without one, and announcing a credential we do not have to
    // endpoints that are perfectly happy anonymously is not a good way to start.
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: 'Bearer ' + token
      }
    }

    return config
  },
  function(error) {
    // Do something with request error

    return Promise.reject(error)
  }
)

client.interceptors.response.use(
  function(config) {
    if (_.get(config, 'config.skipResponseInterceptor')) return config
    const info = _.get(config, 'data.info') ?? {}
    const data = _.get(config, 'data.data')
    if (info.status_code >= 300) {
      store.dispatch('useToast', {
        severity: 'error',
        summary: _.get(info, 'message'),
        detail: _.get(info, 'details')
      })
    } else if (info.message === 'success' && data && _.isString(data)) {
      store.dispatch('useToast', {
        severity: 'success',
        summary: data,
        detail: ''
      })
    }

    return config
  },
  async function(error) {
    if (_.get(error, 'config.skipResponseInterceptor')) {
      return Promise.reject(error)
    }

    // Read through `_.get`. `error.response` is absent whenever the request
    // never got an answer -- a dropped connection, a CORS refusal, a navigation
    // that aborted it -- and reading `.status` off it threw a TypeError from
    // inside this handler, replacing the real failure with a confusing one.
    if (_.get(error, 'response.status') === 400) {
      const details = _.get(error, 'response.data.info.details')
      _.forEach(details, (value, key) => {
        setTimeout(() => {
          store.dispatch('useToast', {
            severity: 'error',
            summary: key,
            detail: value,
            life: 10000
          })
        }, 500)
      })
    }

    if (staleCredential(error)) {
      const original = error.config ?? {}

      // Once. A retry that can itself trigger a retry is a loop, and a second
      // failure means the new token was never the problem.
      if (!original.__retriedAfterRefresh) {
        original.__retriedAfterRefresh = true

        const token = await refreshAccessToken()

        if (token) {
          // The backend rebuilds the claims from the user record on every
          // renewal, so a role change reaches the interface here rather than
          // waiting for the next sign-in.
          await store.dispatch('auth/fetchUser')

          original.headers = {
            ...original.headers,
            Authorization: `Bearer ${token}`
          }

          return client(original)
        }
      }

      // Nothing left to try: no refresh token, one the server refused, or a
      // retry that failed again. Signing in is the only way back, which is what
      // revocation is supposed to mean.
      store.dispatch('auth/logout')
    }

    return Promise.reject(error)
  }
)

export default client
